/**
 * A ponte entre o simulador portado e a camada de dados.
 *
 * MESMO PADRÃO DA `motor-ponte.ts`, E PELO MESMO MOTIVO
 * `persist()` é chamada em **38 lugares** dentro de `simulador.js` — depois de cada
 * tecla, cada célula colada, cada caixa marcada. Reescrever as 38 seria trocar um risco
 * pequeno por 38 chances de esquecer uma. Em vez disso, `persist()` mantém o nome, a
 * assinatura e todos os chamadores; só o **corpo** muda, e o corpo é esta ponte.
 *
 * O QUE MUDA NA PRÁTICA
 * Antes: `persist()` esperava 350 ms e escrevia UM pacote no `localStorage` com TUDO
 * dentro (todos os empreendimentos, todos os cenários, as premissas e o id do ativo).
 * Agora: `persist()` registra a edição no rascunho do cenário aberto, e quem decide
 * quando subir ao servidor é o `rascunho.ts` — que já tem agrupamento próprio, fila por
 * cenário, guarda de rascunho vencido e ligação com o monitor de conexão.
 *
 * ⚠ O agrupamento de 350 ms da v67 SOME de propósito. Ter dois temporizadores
 * independentes tentando salvar a mesma coisa é como se perde trabalho: o de fora
 * dispararia com o de dentro no meio de uma gravação. Um só dono do relógio.
 */
import {
  anotarEdicao, atualizarVersaoBase, descartarRascunho, gravarAgora, regravarForaDaSessao, situacaoRascunho,
} from '../../../../packages/dados/src/rascunho';
import { publicarCenario } from '../../../../packages/dados/src/repositorios';
import type { CenarioResumo } from '../../../../packages/dados/src/repositorios';
import type { EntradaMotor } from '../../../../packages/dados/src/mapeamento';
import {
  avisoAntesDePublicar, complementoDepoisDePublicar, vinculosOrfaos,
} from '../../../../packages/dados/src/vinculos-vagas';
import { avisar } from '../ui/aviso';
import { impressaoDaEntrada, semMudanca } from './impressao-entrada';

export interface ContextoDados {
  /**
   * O que está na tela agora, no formato que o motor e o banco consomem.
   * Quem monta é o arranque do simulador, que é quem conhece o estado global dele.
   */
  entradaAtual(): EntradaMotor;
}

/**
 * O que só a CASCA sabe. Separado do de cima de propósito: `entradaAtual` vem de dentro
 * do simulador (que conhece o estado global dele) e isto vem de fora (que conhece o
 * cenário e a navegação). Um setter só, que substituísse tudo, faria o segundo a chamar
 * apagar o trabalho do primeiro — e a ordem entre eles depende do carregamento do script.
 */
export interface ContextoPublicacao {
  /** O cenário aberto. A versão dele é a trava otimista da publicação. */
  cenario(): CenarioResumo;
  /**
   * Avisa a tela que a publicação deu certo, para o cabeçalho refletir a versão nova. Desde a
   * 0032 o cadastro é de CADA cenário: o que foi publicado não muda nenhum irmão, e por isso não
   * viaja mais aqui.
   */
  aoPublicar?(r: { versao: number; versaoPublicada: number }): void;
}

let ctx: ContextoDados | null = null;
let ctxPub: ContextoPublicacao | null = null;

/** Chamada pelo simulador, no arranque, ANTES de qualquer coisa que possa editar. */
export function configurarDados(c: ContextoDados): void {
  ctx = c;
  /* Contexto novo = tela nova: a referência de "nada mudou" é a desta tela, tirada quando a
     montagem terminar (`retomar`). A da tela anterior não vale para esta. */
  referencia = null;
}

/** Chamada pela casca, antes de carregar o simulador. */
export function configurarPublicacao(c: ContextoPublicacao): void {
  ctxPub = c;
}

/**
 * O que está na tela AGORA (publicado + o que foi digitado e não publicado), ou `null` se o
 * simulador não está montado. É o que o "Copiar" do seletor leva para a cópia quando o
 * copiado é o cenário aberto (pedido da v32: a cópia "nascerá com os mesmos dados da que
 * selecionei para copiar"). Leitura só — não registra edição nem mexe no rascunho.
 */
export function entradaDaTela(): EntradaMotor | null {
  if (ctx === null) return null;
  /* `entradaAtual` devolve as REFERÊNCIAS vivas do simulador (`empAtivo()`, `estado.rascunho.lev`).
     Quem copia segura isto até o próximo cenário montar: uma cópia própria não pode mudar
     junto com a tela. */
  const viva = ctx.entradaAtual();
  try {
    return structuredClone(viva);
  } catch {
    return viva;
  }
}

/**
 * O novo corpo de `persist()`.
 *
 * Erra alto se não estiver configurada, em vez de engolir. Uma edição registrada no
 * vazio é trabalho do usuário perdido em silêncio — o defeito mais caro que esta camada
 * pode ter, e o mais difícil de perceber, porque a tela continua mostrando o que foi
 * digitado. O arranque configura a ponte antes de montar qualquer formulário, então
 * chegar aqui sem contexto é defeito de programação, não estado normal.
 */
/**
 * Durante a MONTAGEM, `persist()` não é edição.
 *
 * `syncOutputs()` termina com `persist()`, e ela roda a cada redesenho — inclusive o
 * primeiro, o da montagem. No `localStorage` isso era grátis. Contra o servidor, virava
 * um rascunho gravado para quem só abriu o cenário e passou os olhos: linha criada, aviso
 * de "recuperamos o que você digitou" na próxima visita, e nada digitado.
 *
 * A supressão é **contada** em vez de booleana porque montar pode aninhar (`montarFormulario`
 * chama coisas que chamam `syncOutputs`), e um booleano seria destravado pelo primeiro
 * `retomar` de dentro, deixando o resto da montagem gravando de novo.
 */
let suspensoes = 0;
let engolidas = 0;
let recusadas = 0;
let iguais = 0;
/**
 * O conteúdo que o rascunho já tem por registrado (lote 4 de 10/10/2026, item 2): o da tela ao
 * fim da montagem — ou da chegada da versão de outra pessoa —, depois o último que o rascunho
 * aceitou e, ao publicar, o que foi publicado. `persist()` com o MESMO conteúdo não é edição.
 * Ver `impressao-entrada.ts`.
 */
let referencia: string | null = null;

export function suspender(): void {
  suspensoes += 1;
}

export function retomar(): void {
  suspensoes = Math.max(0, suspensoes - 1);
  /* Fim da montagem (ou da troca de modelo pela sonda): o que está na tela agora não é edição
     de ninguém aqui — é a referência. Só no último `retomar`: os aninhados ainda estão no meio. */
  if (suspensoes === 0 && ctx !== null) referencia = impressaoDaEntrada(ctx.entradaAtual());
}

/** Quantas chamadas foram ignoradas por estarem em montagem. Só para teste. */
export function edicoesEngolidas(): number {
  return engolidas;
}

/**
 * Quantas chamadas de `persist()` NÃO foram ao rascunho porque o conteúdo era o mesmo já
 * registrado — trocar de sub-aba, abrir e fechar painel, redesenhar. Só para teste.
 */
export function edicoesSemMudanca(): number {
  return iguais;
}

/**
 * Quantas edições o rascunho RECUSOU por não haver sessão do cenário na tela (tela
 * só-leitura, ou que já saiu). Só para teste — é o que prova, no navegador, que o `persist()`
 * da tela só-leitura foi chamado e não foi parar no rascunho de outro cenário.
 */
export function edicoesRecusadas(): number {
  return recusadas;
}

/** De qual cenário é a sessão de rascunho aberta agora (`null` = nenhuma). Só para teste. */
export function sessaoDeRascunho(): string | null {
  return situacaoRascunho().cenarioId;
}

export function persistir(): void {
  if (ctx === null || ctxPub === null) {
    throw new Error('A ponte de dados não foi configurada — chame configurarDados() e configurarPublicacao() no arranque.');
  }
  if (suspensoes > 0) {
    engolidas += 1;
    return;
  }
  /* A edição leva o cenário DA TELA — o que a casca montou —, e o rascunho só a aceita na
     sessão desse cenário. Numa tela só-leitura não há sessão, e a edição não vai a lugar
     nenhum (antes ia para a sessão que tivesse ficado aberta: a de OUTRO cenário).
     A recusa vem ANTES da comparação de propósito: é ela que prova, no navegador, que o
     `persist()` da tela só-leitura foi chamado e não foi parar em lugar nenhum. */
  const cenarioId = ctxPub.cenario().id;
  if (situacaoRascunho().cenarioId !== cenarioId) {
    recusadas += 1;
    return;
  }
  /* Lote 4 de 10/10/2026, item 2: o MESMO conteúdo já registrado não é edição — é desenho
     (sub-aba "Fluxo", abrir/fechar painel). Gravar de novo só criaria a linha do "Recuperamos…". */
  const entrada = ctx.entradaAtual();
  const impressao = impressaoDaEntrada(entrada);
  if (semMudanca(impressao, referencia)) {
    iguais += 1;
    return;
  }
  /* Revisão do lote 4 (10/10/2026): o rascunho guarda a FOTO do que foi comparado (a impressão), não
     o objeto vivo do simulador. A gravação sai 800 ms depois (ou mais, com rede lenta/offline) e
     levaria o que estivesse no modelo NAQUELA hora — uma célula do Detalhamento mudada sem persist();
     se a pessoa a desfizesse, o persist() seguinte seria ignorado (igual à referência) e o banco
     ficaria com o estado que ela desfez. Assim a referência e o que vai ao banco são a mesma coisa.
     O `JSON.parse` da impressão é exatamente o que o envio mandaria; sem impressão, a regra antiga. */
  const foto = impressao !== null ? (JSON.parse(impressao) as EntradaMotor) : entrada;
  if (!anotarEdicao(cenarioId, foto)) {
    recusadas += 1;
    return;
  }
  referencia = impressao;
  /* APC-3: o que foi digitado no cenário que está SENDO publicado, durante a ida — se a tela sair
     antes de a publicação voltar, é isto que `publicar()` regrava sobre a versão nova. */
  if (cenarioId === publicandoId) anotadaNaIda = { foto, impressao };
}

/**
 * Revisão do lote 4 (10/10/2026), resíduo do item 2: abrir "Demanda por unidade" ou "Detalhar" com a
 * tabela vazia cria uma linha vazia por unidade (`initDemDet`/`initUnDet`, herdado da v67). Isso é
 * DESENHO, não edição — mas a comparação por conteúdo via as linhas novas como mudança, gravava o
 * rascunho e a próxima abertura dizia "Recuperamos o que você tinha digitado".
 *
 * Roda `fn` e, SE a tela estava igual ao já registrado antes dela, a referência passa a incluir o que
 * ela materializou. Havendo mudança ainda não registrada antes, nada muda: o próximo `persist()`
 * grava tudo (a mudança pendente não é engolida). Só para os caminhos de ABRIR — "Limpar dados" e
 * importações também chamam `initDemDet`/`initUnDet`, e ali esvaziar é edição.
 */
export function semEdicao<T>(fn: () => T): T {
  const antes = ctx === null ? null : impressaoDaEntrada(ctx.entradaAtual());
  const r = fn();
  if (ctx !== null && semMudanca(antes, referencia)) referencia = impressaoDaEntrada(ctx.entradaAtual());
  return r;
}

/* ============================================================================
 * PUBLICAR — o "Salvar cenário" do simulador
 * ========================================================================== */

let publicando = false;
/** APC-3: o cenário cuja publicação está no ar (`null` fora da ida ao servidor). */
let publicandoId: string | null = null;
/** APC-3: a última edição ACEITA desse cenário durante a ida — ver `persistir`. */
interface AnotadaNaIda { foto: EntradaMotor; impressao: string | null }
let anotadaNaIda: AnotadaNaIda | null = null;
/* Lida por função: depois do `anotadaNaIda = null` antes do `await`, o TypeScript estreita a variável
   para `null` e não sabe que o `persistir()` a preencheu durante a ida. */
const lerAnotadaNaIda = (): AnotadaNaIda | null => anotadaNaIda;

/**
 * Publica o cenário. É o novo corpo do `salvarCenario()` do simulador.
 *
 * O QUE ACONTECE, E POR QUE NESTA ORDEM
 *  1. **Descarrega o rascunho primeiro.** Publicar com edição ainda em voo criaria a
 *     situação absurda de a versão publicada ser mais VELHA que o rascunho pessoal.
 *  2. Publica. `publicarCenario` faz duas chamadas atômicas, e o par não é atômico —
 *     por isso o estado intermediário (VERSAO_NAO_REGISTRADA) tem mensagem própria.
 *  3. Atualiza a versão-base, senão a próxima publicação bate na trava com a versão velha.
 *  4. **Descarta o rascunho** — se a tela ainda é o que foi publicado: o digitado virou versão
 *     publicada, e manter o rascunho faria a próxima abertura oferecer "recuperar" um trabalho já
 *     publicado. Se a pessoa digitou DURANTE a ida, o rascunho fica, regravado sobre a versão nova
 *     (CAL-4).
 *
 * `publicando` impede a segunda chamada enquanto a primeira está no ar: dois cliques em
 * "Salvar" mandariam a mesma versão-base duas vezes, e a segunda bateria em conflito
 * dizendo que "alguém publicou" — sendo que esse alguém era o próprio usuário.
 */
export async function publicar(): Promise<void> {
  /* APC-3 (revisão de 12/10/2026): as referências DESTA tela, tiradas antes de qualquer ida ao
     servidor. `ctx`/`ctxPub` são do módulo e trocam quando outra tela do simulador monta
     (`configurarPublicacao` na montagem); relidas depois do `await`, a versão de A ia parar no
     cabeçalho e na trava de B. `pub` é também o marcador de "ainda é a mesma tela". */
  const dados = ctx;
  const pub = ctxPub;
  if (dados === null || pub === null) {
    throw new Error('A ponte de dados não foi configurada — faltou configurarDados() ou configurarPublicacao().');
  }
  if (publicando) return;
  publicando = true;
  try {
    const situacao = await gravarAgora();
    if (situacao.conexao.salvamento === 'pendente' || situacao.conexao.rede === 'offline') {
      avisar('Sem conexão para publicar. O que você digitou está guardado e sobe sozinho quando a rede voltar.', 'aviso');
      return;
    }
    /* APC-3: a pessoa saiu para OUTRO simulador enquanto o rascunho subia. O que `entradaAtual`
       lê agora (o estado global do simulador) já é o da outra tela — publicar seria gravar o
       conteúdo dela neste cenário. Não publica, e diz; o digitado subiu no `gravarAgora` acima. */
    if (ctxPub !== pub) {
      avisar('A publicação não foi feita: você saiu do cenário antes de ela começar. O que você digitou continua guardado como alteração não publicada.', 'aviso');
      return;
    }

    const cenario = pub.cenario();
    const entrada = dados.entradaAtual();
    /* Item 19 (09/10/2026): vínculo de vaga que aponta para vaga que não existe mais não tem
       para onde ir no banco — `paraBanco` o pula. Pulava CALADO, e foi assim que uma versão saiu
       com zero vínculos sem ninguém ver. Agora avisa antes e repete no aviso de sucesso (o toast
       é um só: o de antes some quando o de "Publicado" chega). Publica mesmo assim: bloquear
       travaria quem nem está mexendo em vagas. */
    const orfaos = vinculosOrfaos(entrada);
    const nOrfaos = orfaos.dem + orfaos.det;
    if (nOrfaos > 0) avisar(avisoAntesDePublicar(orfaos), 'aviso');   /* diz em qual tabela (revisão do lote 1, 10/10/2026) */
    /* Lote 4 (item 2): a impressão do que VAI para o banco, tirada agora — o objeto é vivo, e o
       que for digitado durante a ida não pode passar por "já registrado". */
    const impressaoPublicada = impressaoDaEntrada(entrada);
    publicandoId = cenario.id;
    anotadaNaIda = null;
    const r = await publicarCenario({ cenario, entrada });

    /* Rascunho que não vai embora não desfaz a publicação — o pior efeito é a próxima abertura
       oferecer recuperar algo já publicado. Não vale derrubar o sucesso. */
    const descartar = (): Promise<void> => descartarRascunho(cenario.id).catch(() => undefined);
    /* APC-3: regrava o digitado na ida sobre a versão NOVA, sem depender da sessão (a tela saiu). */
    const regravar = (foto: EntradaMotor): void => {
      regravarForaDaSessao({ cenarioId: cenario.id, empresaId: cenario.empresaId, baseVersao: r.versao, entrada: foto });
    };

    /* CAL-4 (revisão de 11/10/2026), NESTA ORDEM:
       (a) a versão-base nova PRIMEIRO — o que for anotado daqui em diante já nasce sobre ela;
       (b) a impressão da tela AGORA (a pessoa pode ter digitado durante a ida, 1 a 2 s);
       (c) igual ao que foi publicado: o rascunho virou passado — descarta, como sempre;
       (d) diferente: NÃO descarta. Anota de novo a foto da tela, que troca na fila o item da
           versão-base VELHA (que a próxima abertura descartaria como vencido) pelo mesmo conteúdo
           sobre a versão nova, e já manda gravar — um F5 logo depois não perde o valor. */
    atualizarVersaoBase(cenario.id, r.versao);
    if (ctxPub === pub) {
      /* A MESMA tela (nenhum outro simulador montou): o estado global ainda é o deste cenário. */
      const impressaoAgora = impressaoDaEntrada(dados.entradaAtual());
      if (semMudanca(impressaoAgora, impressaoPublicada)) {
        await descartar();
        /* O publicado é o novo "nada mudou": sem rascunho, a tela igual a ele não grava de novo. */
        referencia = impressaoPublicada;
      } else {
        const foto = impressaoAgora !== null ? (JSON.parse(impressaoAgora) as EntradaMotor) : dados.entradaAtual();
        if (anotarEdicao(cenario.id, foto)) {
          referencia = impressaoAgora;
          void gravarAgora();
        } else {
          /* APC-3: sem sessão deste cenário — a pessoa saiu para uma tela sem simulador (a lista,
             a Equipe) durante a ida. A edição seria recusada e o digitado ficaria vencido. */
          regravar(foto);
        }
      }
      pub.aoPublicar?.({ versao: r.versao, versaoPublicada: r.versaoPublicada });
    } else if (ctxPub?.cenario().id !== cenario.id) {
      /* APC-3: OUTRO cenário montou durante a ida. Nada da tela de agora entra aqui — nem a
         versão (era o `aoPublicar` relido que punha a versão de A no cabeçalho e na trava de B),
         nem a comparação de impressões (o estado global já é o de B). O rascunho DESTE cenário:
         com o que foi digitado na ida, regravado sobre a versão nova; sem nada, descartado. */
      const naIda = lerAnotadaNaIda();
      if (naIda !== null && !semMudanca(naIda.impressao, impressaoPublicada)) regravar(naIda.foto);
      else await descartar();
    }
    /* (else: a tela DESTE cenário foi montada de novo durante a ida — ela abriu a própria sessão de
       rascunho e a própria versão, e a sonda dela traz a publicação. Mexer daqui seria disputar o
       rascunho com ela.) */
    avisar(`Publicado como versão ${r.versaoPublicada}. Agora todo mundo na incorporadora vê.${complementoDepoisDePublicar(orfaos)}`,
      nOrfaos > 0 ? 'aviso' : 'ok');
  } catch (e) {
    avisar(mensagemDePublicacao(e), 'erro');
  } finally {
    publicando = false;
    publicandoId = null;
    anotadaNaIda = null;
  }
}

/**
 * As três falhas de publicação pedem AÇÕES DIFERENTES do usuário, e mandar todas com a
 * mesma frase é o que faz alguém repetir a operação errada — inclusive refazer um
 * salvamento que já valeu.
 */
function mensagemDePublicacao(e: unknown): string {
  const codigo = (e as { codigo?: string } | null)?.codigo;
  if (codigo === 'CONFLITO_VERSAO') {
    return 'Alguém publicou este cenário enquanto você editava. Reabra o cenário para ver o que mudou antes de publicar de novo — publicar agora apagaria o trabalho da outra pessoa.';
  }
  /* VERSAO_NAO_REGISTRADA já vem com a frase certa de `repositorios.ts`, que sabe exatamente o
     que ficou gravado e o que não ficou. Repetir a explicação aqui só criaria duas verdades.
     O mesmo vale, desde 09/10/2026, para CENARIO_ARQUIVADO / CENARIO_INEXISTENTE: é o
     conflito que `explicarConflito` reconheceu como "alguém arquivou ou excluiu", e a frase
     de "alguém publicou" acima seria falsa — e, desde a 0032, para PACOTE_DESATUALIZADO e
     CADASTRO_INVALIDO (o campo que o banco recusou). O CADASTRO_NAO_SALVO deixou de existir: o
     cadastro vai no mesmo `salvar_cenario` do cenário. Todos caem aqui. */
  return e instanceof Error ? e.message : String(e);
}
